import { createContext, ReactNode, useContext } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { InsertUser, User as SelectUser } from "@shared/schema";
import { queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { auth } from "@/api/config.ts";

type AuthContextType = {
  user: SelectUser | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<SelectUser, Error, InsertUser>;
};

type LoginData = Pick<InsertUser, "username" | "password">;

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();

  // Consulta para obter o usuário atual - use a mesma função auth do config
  const {
    data: user,
    error,
    isLoading,
  } = useQuery<SelectUser | null, Error>({
    queryKey: ["/api/user"],
    queryFn: async () => {
        const { data } = await auth.user();
        return data;
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      const { data } = await auth.login(credentials);
      console.log("Login bem-sucedido, dados do usuário:", data.user);
      return data.user || data; // Depende da estrutura de resposta do seu backend
    },
    onSuccess: (userData: SelectUser) => {
      console.log("Definindo dados do usuário no cache:", userData);

      queryClient.setQueryData(["/api/user"], userData);
      // Após login bem-sucedido, revalide explicitamente o estado do usuário
      queryClient.invalidateQueries({queryKey: ["/api/user"]});

      toast({
        title: "Login bem-sucedido",
        description: `Bem-vindo(a) de volta, ${userData.firstName}!`,
      });
    },
    onError: (error: Error) => {
      console.error("Erro no login:", error);
      toast({
        title: "Falha no login",
        description: "Nome de usuário ou senha incorretos.",
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (credentials: InsertUser) => {
      const { data } = await auth.register(credentials);
      return data.user || data; // Ajuste conforme sua API
    },
    onSuccess: (userData: SelectUser) => {
      queryClient.setQueryData(["/api/user"], userData);
      toast({
        title: "Registro bem-sucedido",
        description: `Bem-vindo(a) ao Therapy Hub, ${userData.firstName}!`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Falha no registro",
        description: "Nome de usuário já existe ou dados inválidos.",
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await auth.logout();
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/user"], null);
      toast({
        title: "Logout bem-sucedido",
        description: "Sessão encerrada com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Falha no logout",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}